import { useState } from 'react';

import chapterImg1 from '../../../../assets/images/courses/teamplayer&collaboration/chapter3.1.jpg';
import chapterImg2 from '../../../../assets/images/courses/teamplayer&collaboration/chapter3.2.1.jpg';
import chapterImg3 from '../../../../assets/images/courses/teamplayer&collaboration/chapter3.2.2.jpg';
import chapterImg4 from '../../../../assets/images/courses/teamplayer&collaboration/chapter3.2.3.jpg';
import introImg from '../../../../assets/images/courses/teamplayer&collaboration/chapter3.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';
import { RatingTable, TopFiveResults } from '../../RoleCategory';
import { TabsBlock } from '../../TabsBlock';

interface TeamPlayersAndCollaborationChapter1 {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersAndCollaborationChapter3: React.FC<TeamPlayersAndCollaborationChapter1> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const [ratings, setRatings] = useState<{ [key: string]: number } | null>(null);

  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const showYoureListeningTabData = [
    {
      title: 'COMMIT AND DELIVER:',
      mediaImage: chapterImg2,

      htmlContent: `<p>If you make a commitment, follow through. If you anticipate being unable to fulfill a task, communicate this early on. Inform the person of any delays or seek assistance if needed. Don't hesitate to ask for support if you're unsure how to proceed.</p>`,
    },
    {
      title: 'PUT IT IN WRITING:',
      mediaImage: chapterImg3,
      htmlContent: `<p>Documentation is key to avoiding misunderstandings. When discussing tasks or deadlines, ensure clarity by documenting expectations through email or other written means. Agree on the specifics of what needs to be done, the deadline, and who is responsible.</p>`,
    },
    {
      title: 'SET BOUNDARIES:',
      mediaImage: chapterImg4,
      htmlContent: `<p>Establish boundaries to manage your workload effectively. If you're already at full capacity and receive an urgent task, communicate your situation to the team. Initially express willingness to help, then request assistance in reprioritizing tasks. This approach encourages collaboration and ensures that your workload is manageable.</p>`,
    },
  ];

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  const columns = ['Yes', 'No'];
  const roleCategories = [
    {
      title: '',
      roles: [
        {
          name: '1. ',
          description: 'Do I keep my promises and commitments to my team members?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '2. ',
          description: 'Do I follow through on tasks and responsibilities in a timely manner?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '3. ',
          description:
            'Do I document accountabilities and email expectations before starting on a task?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '4',
          description: 'Do I respect the boundaries and confidentiality of others?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '5. ',
          description: 'Do I admit mistakes and take responsibility for my actions?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '6. ',
          description: ' Do I actively listen and seek to understand the perspectives of others?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '7. ',
          description:
            'Do I manage expectations and clearly communicate if I know in advance that a task won´t be completed to schedule?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '8. ',
          description: 'Do I maintain honesty and integrity in my interactions with team members?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '9. ',
          description:
            'Have I demonstrated through my actions that I´m someone others can rely on to do what I say I will do?',
        },
      ],
    },
    {
      title: '',
      roles: [
        {
          name: '10. ',
          description:
            '"I sometimes struggle with giving and receiving feedback, which can lead to misunderstandings and a lack of growth."',
        },
      ],
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Manage Expectations & Foster Trust</h2>

        <MediaBlock mediaImage={introImg}>
          <p className="text-3xl leading-10 font-light w-3/4">
            "Trust is the glue of life. It's the most essential ingredient in effective
            communication. It's the foundational principle that holds all relationships."
            <br />— Stephen R. Covey
          </p>
        </MediaBlock>
        <br />

        <p className="text-lg font-normal text-center mt-7 mb-20 leading-10">
          In any relationship, whether it's at work or in personal life, unmet expectations often
          spark friction. Even if you bring a lot of potential to the table, if the expectations
          within your team aren't addressed, you risk losing their trust.
        </p>

        <p className="text-xl font-bold text-left mt-7 mb-5 leading-10">Why Trust is Important</p>
        <p className="text-lg font-normal text-left mt-7 mb-20 leading-10">
          Trust is the cornerstone of all successful relationships, whether personal, professional,
          or within a team. It creates a safe environment where people feel confident to share
          ideas, take risks, and collaborate effectively. When trust is present, communication
          becomes more open and honest, leading to better problem-solving and innovation. Without
          trust, relationships can become strained, leading to misunderstandings, reduced
          cooperation, and even conflict. In any group, building and maintaining trust is crucial
          for long-term success and positive outcomes.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-5">Types of Team Trust</h3>
        <p className="text-lg font-normal text-left mt-7 mb-14 leading-10">
          Rebuilding trust can be a lengthy process once it's been broken. It's crucial to
          communicate openly and manage expectations effectively to maintain strong relationships
          and a positive team dynamic.
        </p>
        <p className="text-xl font-bold text-left mt-7 mb-5 leading-10">
          In teams, trust comes in two forms of trust:
        </p>

        <ul className="list-decimal pl-5 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">Integrity-based trust, where I trust your motives.</li>
          <li className="pl-9 mb-7">Competence-based trust, where I trust your skills.</li>
        </ul>
        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <strong>Both forms of trust are essential. </strong>
            <br />
            For instance, if you're given a five-day deadline and you deliver in three, you're
            showcasing both integrity-based and competence-based trust by meeting the deadline
            effectively.
            <br />
            However, if you promise to finish the task in two days but it takes three, although the
            actual time taken remains three days, you've breached integrity-based trust. By failing
            to manage expectations, you've compromised your reliability and credibility.
          </p>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-5">
          Rules to Maintaining Your Trustworthiness
        </h3>
        <p className="text-lg font-normal text-left mt-7 mb-10 leading-10">
          Here are three rules to maintain your trustworthiness:
        </p>
        <TabsBlock tabsData={showYoureListeningTabData} />

        <p className="text-xl font-normal text-center mt-7 mb-20 leading-10">
          Remember, maintaining trust in teams requires integrity and consistent communication of
          expectations. Be someone dependable and transparent in your actions. What is your
          trustworthiness? Complete the assessment below to learn more.
        </p>

        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-5">
          Trustworthiness Self-Assessment
        </h3>
        <p className="text-lg font-normal text-center mt-7 mb-20 leading-10">
          Complete the trustworthiness assessment and identify areas for improvement.
        </p>
        <div className="p-6 bg-white rounded-lg shadow-md mb-6 mt-6">
          {ratings ? (
            <TopFiveResults
              ratings={ratings}
              roleCategories={roleCategories}
              onRetake={() => setRatings(null)}
              showAllRatedRecords={5}
            />
          ) : (
            <RatingTable
              roleCategories={roleCategories}
              onSubmit={setRatings}
              column={columns}
              firstColumn="Questions"
            />
          )}
        </div>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mt-10 mb-5">
          Developing Trustworthiness
        </h3>

        <p className="text-lg font-normal text-left mt-7 mb-10 leading-10">
          By regularly assessing yourself against these questions and striving for improvement, you
          can enhance your effectiveness as a team member and leader.
        </p>
        <p className="text-lg font-normal text-left mt-7 mb-10 leading-10">
          Here are some simple suggestions to help you cultivate your trustworthiness.
        </p>
        <div className="flex flex-col items-center relative min-h-screen py-10">
          {/* Vertical Line (Stops before the last div) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-200 h-[calc(100%-100px)]"></div>

          {/*drop down Cards */}
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Communicate openly and honestly:
            </h3>
            <p className="text-xl font-light mb-8">
              Establish open and honest communication with your teammates. This not only helps to
              foster trust, but it also improves teamwork.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Follow through on commitments:
            </h3>
            <p className="text-xl font-light mb-8">
              Keep your promises and commitments to your teammates and follow through on tasks and
              responsibilities in a timely manner.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Manage expectations early:
            </h3>
            <p className="text-xl font-light mb-8">
              If you know in advance that you will be unable to meet a deadline or complete
              something you committed to, communicate early and seek support, if needed.
            </p>
          </div>

          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Maintain confidentiality:
            </h3>
            <p className="text-xl font-light mb-8">
              Respect the confidentiality of your teammates and maintain appropriate boundaries.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Admit mistakes and take responsibility:
            </h3>
            <p className="text-xl font-light mb-8">
              Admit mistakes and take responsibility for your actions. This can help to build trust
              and improve teamwork.
            </p>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl mb-10">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Document accountabilities:
            </h3>
            <p className="text-xl font-light mb-8">
              Capture accountabilities and email expectations so that you have a documented record.
              Gain agreement on the three Ws:
            </p>
            <ul className="list-decimal pl-5 mb-14 text-xl font-light">
              <li>What specifically needs to be done?</li>
              <li>When is the deadline?</li>
              <li> Who is responsible?</li>
            </ul>
          </div>
          <div className="relative custom-tab p-5 w-[75%] border border-1 bg-[#f8f8f8] border-l-purple-600 border-l-4 rounded-xl">
            <h3 className="text-xl text-purple-600 text-left font-bold mb-5">
              Encourage open communication:
            </h3>
            <p className="text-xl font-light mb-8">
              Encourage open communication with others and actively listen to the thoughts and
              concerns of your teammates.
            </p>
          </div>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold m-5">Reflect</h3>
        <p className="text-lg font-light flex-1 mt-5 mb-5">
          Reflect and answer the question below. You may print your response upon completion.
        </p>
        <br />

        <div id="print-view" className="mt-5 mb-5">
          <ol className=" pl-5 mb-14 text-xl font-light">
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  What commitments will you make to cultivate greater trustworthiness and to become
                  a person of integrity that others can rely on?
                </p>
                <textarea
                  className="w-full h-32 p-2 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <br />

        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Foster Psychological Safety
        </button>
      </div>
    </>
  );
};
